import { AudioClip, AudioSource, Button, instantiate, Node, Prefab, ScrollView, _decorator } from 'cc';
import AudioManager from '../../../AudioManager';
import Game from '../../../Game';
import ItemGenerator from '../../Components/Misc/ItemGenerator';
import MultiThemeLabel from '../../Components/MultiTheme/MultiThemeLabel';
import InfiniteScrollView from '../../Components/ScrollView/InfiniteScrollView';
import ValueToggleSelector from '../../Components/ValuePicker/ValueToggleSelector';
import ValueSelector from '../../Helper/ValueSelector';
import BaseTool from '../BaseTool';
import AudioClipItem from './AudioClipItem';
import AudioClipItemView from './AudioClipItemView';

enum SortType {
    Type,
    Name,
    Time,
}

const { ccclass, property } = _decorator;

export const VOLUME_BASE = 100;

@ccclass('AudioInspector')
export default class AudioInspector extends BaseTool {
    @property(Node)
    private readonly mainRoot: Node = null;
    @property(Prefab)
    private readonly itemPrefab: Prefab = null;
    @property(Button)
    private readonly resetButton: Button = null;
    @property(InfiniteScrollView)
    private readonly listView: InfiniteScrollView = null;
    @property(Node)
    private readonly hint: Node = null;
    @property(ValueToggleSelector)
    private readonly sortTypeSelector: ValueToggleSelector = null;

    // protected audioSourceMap: Record<string, AudioSource> = {};
    protected itemMap: Record<string, AudioClipItem> = {};
    protected itemNameList: string[] = [];
    private _musicAudioItem: AudioClipItemView = null;
    private _effectAudioItem: AudioClipItemView = null;
    private _scrollView: ScrollView = null;
    private _sortType: SortType = SortType.Type;
    private _inverse: boolean = false;
    private _compareFunc: (a: AudioClipItem, b: AudioClipItem) => number = this._getCompareFunction();

    protected get itemList() {
        return this._scrollView.content.getComponentsInChildren(AudioClipItemView);
    }

    protected onLoad() {
        this._scrollView = this.listView.getComponent(ScrollView);

        this.resetButton.on(Button.EventType.CLICK, this.resetAllVolume, this);

        const sortTypeValueSelector = new ValueSelector(Object.values(SortType).filter((type) => typeof type === 'number') as SortType[]);
        const sortTypeButtonMap: Map<Node, Button> = new Map();
        const sortTypeButtonLabelMap: Map<Node, MultiThemeLabel> = new Map();
        this.sortTypeSelector.init(
            sortTypeValueSelector,
            (option: Node) => {
                sortTypeButtonMap.set(option, option.getComponent(Button));
                sortTypeButtonLabelMap.set(option, option.getComponentInChildren(MultiThemeLabel));
            },
            (option, isSelected) => {
                if (isSelected) {
                    this._sortType = sortTypeValueSelector.value;
                    this._inverse = sortTypeButtonLabelMap.get(option).theme === 1;
                    this._compareFunc = this._getCompareFunction();
                    this._updateDisplayAudios();
                }
                sortTypeButtonMap.get(option).keep = isSelected;
            },
        );
        this.sortTypeSelector.on(ValueToggleSelector.EVENT_TYPE.BUTTON_CLICK, (button: Button, clickOnSameButton: boolean) => {
            if (clickOnSameButton) {
                const newTheme = (sortTypeButtonLabelMap.get(button.node).theme + 1) % 2;
                sortTypeButtonLabelMap.get(button.node).theme = newTheme;
                this._inverse = newTheme === 1;
                this._compareFunc = this._getCompareFunction();
                this._updateDisplayAudios();
            }
        });

        Game.audio.on(AudioManager.EVENT_TYPE.PLAY_SOUND_EFFECT, this.enableAudioSource, this);
        Game.audio.on(AudioManager.EVENT_TYPE.STOP_SOUND_EFFECT, this.disableAudioSource, this);

        this._createMainVolume('音樂');
        this._createMainVolume('音效');
        this._scrollView.content.on(ItemGenerator.EVENT_TYPE.ITEM_GENERATED, this._onRecordGenerated, this);
        this.listView.on(InfiniteScrollView.EVENT_TYPE.FIRST_ITEM_INDEX_CHANGED, this._updateDisplayAudios, this);
        // handle existing audio sources
        [...Game.audio.musicAudioSources, ...Game.audio.playingEffects].filter(Boolean).forEach((audioSource) => {
            this.enableAudioSource(audioSource);
        });
    }

    protected enableAudioSource(audioSource: AudioSource, clip?: AudioClip) {
        if (!audioSource) {
            return;
        }
        const gameVolume = audioSource.type === AudioSource.AudioSourceType.Music ? Game.audio.musicVolume : Game.audio.effectVolume;
        const volume = gameVolume * Game.audio.filterVolume(audioSource);
        const name = clip?.name ?? audioSource.clip?.name ?? audioSource.name;
        if (!name) {
            throw new Error(`can't find audio clip name for audio source ${audioSource.name}`);
        }
        if (name in this.itemMap) {
            this.itemMap[name].addAudioSource(audioSource);
        } else {
            const newItem = new AudioClipItem();
            newItem.init(audioSource, name, volume);
            this.itemMap[name] = newItem;
            this.itemNameList.push(name);
            this.listView.itemCount = this.itemNameList.length;
        }
        this.updateAudioSource(name);
        this._updateDisplayAudios();
    }

    protected disableAudioSource(audioSource: AudioSource) {
        if (!audioSource) {
            return;
        }
        const name = audioSource.clip?.name;
        if (!name) {
            throw new Error(`can't find audio clip name for audio source ${audioSource.name}`);
        }
        this.itemMap[name]?.deleteAudioSource(audioSource);
        this.updateAudioSource(name);
    }

    protected updateAudioSource(audioName: string) {
        this.itemList.forEach((itemView) => {
            if (itemView.currentItem.audioName === audioName.split('<')[0]) {
                itemView.updateAudioCount();
                itemView.setVolume();
            }
        });
    }

    protected resetAllVolume() {
        Object.values(this.itemMap).forEach((itemData) => {
            itemData.resetVolume();
        });
        this._musicAudioItem.resetVolume();
        this._effectAudioItem.resetVolume();
        this.itemList.forEach((itemView) => {
            itemView.resetVolume();
        });
    }

    private _createMainVolume(name: string) {
        const mainVolume = instantiate(this.itemPrefab).getComponent(AudioClipItemView);
        mainVolume.node.setParent(this.mainRoot);
        const mainAudioItem = new AudioClipItem();
        mainAudioItem.init(null, name, VOLUME_BASE);
        mainVolume.updateView(mainAudioItem);
        // @ts-expect-error
        mainVolume.updateStatus(true);
        // @ts-expect-error
        mainVolume.updateVolumeDisplay(100);
        mainVolume.audioLimitNum.node.parent.active = false;

        if (name === '音樂') {
            this._musicAudioItem = mainVolume;
            mainVolume.setVolume = function setVolume(this: AudioClipItemView) {
                const volume = this.currentItem.isMuted ? 0 : this.volume / VOLUME_BASE;
                Game.audio.musicVolume = volume;
            };
        } else if (name === '音效') {
            this._effectAudioItem = mainVolume;
            mainVolume.setVolume = function setVolume(this: AudioClipItemView) {
                const volume = this.currentItem.isMuted ? 0 : this.volume / VOLUME_BASE;
                Game.audio.effectVolume = volume;
            };
        }
        this.itemMap[name] = mainAudioItem;
    }

    private _updateDisplayAudios() {
        const audioClipItems = Object.values(this.itemMap).filter((item) => item.audioName !== '音樂' && item.audioName !== '音效');
        audioClipItems.sort(this._compareFunc);
        this.itemList.forEach((item, index) => {
            const data = audioClipItems[index + this.listView.firstItemIndex];
            if (!data) {
                return;
            }
            item.updateView(data);
        });
    }

    private _onRecordGenerated(node: Node) {
        const itemView = node.getComponent(AudioClipItemView);
        itemView.updateView(this.itemMap[this.itemNameList[this.itemNameList.length - 1]]);
        itemView.audioLimitNum.node.on(Node.EventType.MOUSE_ENTER, () => {
            this.hint.active = true;
            this.hint.setWorldPosition(node.worldPosition.x - node.worldScale.x * 245, node.worldPosition.y + node.worldScale.y * 20, 0);
        });
        itemView.audioLimitNum.node.on(Node.EventType.MOUSE_LEAVE, () => {
            this.hint.active = false;
        });
    }

    private _getCompareFunction() {
        const inverse = this._inverse ? -1 : 1;
        if (this._sortType === SortType.Type) {
            return (a: AudioClipItem, b: AudioClipItem) => {
                if (a.type !== b.type) {
                    return (a.type - b.type) * inverse;
                }
                return a.audioName.localeCompare(b.audioName);
            };
        }
        if (this._sortType === SortType.Name) {
            return (a: AudioClipItem, b: AudioClipItem) => a.audioName.localeCompare(b.audioName) * inverse;
        }
        return (a: AudioClipItem, b: AudioClipItem) => (b.lastPlayedAt - a.lastPlayedAt) * inverse;
    }
}
